import type {
  ImportMapping,
  ImportParseResponse,
  ImportPreviewRow,
  ImportValidateResponse,
} from "./externalRegisterImportTypes";
import {
  buildExternalRegisterImportStorageKey,
  getBrowserStorage,
  safeJsonParse,
  safeJsonSetItem,
  safeStorageGetItem,
  safeStorageRemoveItems,
} from "../shared/externalRegisterStorage";

export const LEGACY_EXTERNAL_REGISTER_IMPORT_DRAFT_KEY = "external-register-import-active-draft-v1";
export const EXTERNAL_REGISTER_IMPORT_DRAFT_KEY = buildExternalRegisterImportStorageKey("external-register-import-active-draft-v1");
export const EXTERNAL_REGISTER_IMPORT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export type ExternalRegisterImportDraft = {
  updatedAt: number;
  clientImportId: string;
  csvText: string;
  sourceFileName?: string | null;
  sourceFileSize?: number | null;
  hasHeader: boolean | null;
  mapping: ImportMapping;
  parseResponse: ImportParseResponse | null;
  validateResponse: ImportValidateResponse | null;
  approvedRowIds: string[];
  excludedRowIds: string[];
  selectedPatientIds: Record<string, string>;
  previewRows: ImportPreviewRow[];
};

export const sessionDraftAdapter = {
  load(): ExternalRegisterImportDraft | null {
    const storage = getBrowserStorage("session");
    const stored = safeStorageGetItem(storage, EXTERNAL_REGISTER_IMPORT_DRAFT_KEY)
      ?? safeStorageGetItem(storage, LEGACY_EXTERNAL_REGISTER_IMPORT_DRAFT_KEY);
    const parsed = safeJsonParse<ExternalRegisterImportDraft>(stored);
    if (!parsed) {
      if (stored) this.clear();
      return null;
    }

    if (Date.now() - parsed.updatedAt > EXTERNAL_REGISTER_IMPORT_IDLE_TIMEOUT_MS) {
      this.clear();
      return null;
    }
    return parsed;
  },

  save(draft: Omit<ExternalRegisterImportDraft, "updatedAt">): void {
    const storage = getBrowserStorage("session");
    const updatedAt = Date.now();
    const saved = safeJsonSetItem(
      storage,
      EXTERNAL_REGISTER_IMPORT_DRAFT_KEY,
      {
        ...draft,
        updatedAt,
      },
    );
    if (saved) return;

    safeJsonSetItem(
      storage,
      EXTERNAL_REGISTER_IMPORT_DRAFT_KEY,
      {
        ...draft,
        validateResponse: draft.validateResponse
          ? { ...draft.validateResponse, preview: [] }
          : null,
          previewRows: [],
          updatedAt,
      },
    );
  },

  clear(): void {
    safeStorageRemoveItems(getBrowserStorage("session"), [
      EXTERNAL_REGISTER_IMPORT_DRAFT_KEY,
      LEGACY_EXTERNAL_REGISTER_IMPORT_DRAFT_KEY,
    ]);
  },
};
